import React, { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api, normalizeListResponse } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

export default function Inventory() {
  const { token } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [newRow, setNewRow] = useState({
    product_id: '',
    size: '',
    quantity: 1,
    low_stock_threshold: 1
  });

  const loadInventory = async () => {
    try {
      setError('');
      setLoading(true);
      const data = await api.get('/inventory', token);
      setInventory(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    const data = await api.get('/products', token);
    setProducts(normalizeListResponse(data));
  };

  useEffect(() => {
    loadInventory();
    loadProducts();
  }, [token]);

  const stats = useMemo(() => {
    const totalUnits = inventory.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const productStyles = new Set(inventory.map((item) => item.product_id)).size;
    const lowStock = inventory.filter(
      (item) =>
        Number(item.quantity || 0) > 0 &&
        Number(item.quantity || 0) <= Number(item.low_stock_threshold || 0)
    ).length;
    const soldOut = inventory.filter((item) => Number(item.quantity || 0) === 0).length;

    return { totalUnits, productStyles, lowStock, soldOut };
  }, [inventory]);

  const updateItem = async (id, updates) => {
    try {
      setSavingId(id);
      setError('');
      await api.patch(`/inventory/${id}`, updates, token);
      setStatus('Inventory updated');
      await loadInventory();
    } catch (err) {
      setError(err.message || 'Failed to update inventory');
    } finally {
      setSavingId(null);
    }
  };

  const removeItem = async (item) => {
    try {
      setSavingId(item.id);
      setError('');
      await api.delete(`/inventory/${item.id}`, token);
      setStatus(`Removed size ${item.size} for ${item.product_name}`);
      await loadInventory();
    } catch (err) {
      setError(err.message || 'Failed to remove inventory row');
    } finally {
      setSavingId(null);
      setConfirmModal(null);
    }
  };

  const requestRemoveItem = (item) => {
    setConfirmModal({
      title: 'Remove inventory size?',
      message: `Remove size "${item.size}" for ${item.product_name}? This deletes the inventory row only. Past orders are not affected.`,
      confirmLabel: 'Remove size',
      destructive: true,
      onConfirm: () => removeItem(item)
    });
  };

  const addSizeRow = async () => {
    if (!newRow.product_id || !newRow.size) {
      setError('Choose a product and type a size first.');
      return;
    }

    try {
      setSavingId('new');
      setError('');
      await api.post('/inventory', {
        product_id: newRow.product_id,
        size: newRow.size,
        quantity: Number(newRow.quantity),
        low_stock_threshold: Number(newRow.low_stock_threshold)
      }, token);

      setNewRow({
        product_id: '',
        size: '',
        quantity: 1,
        low_stock_threshold: 1
      });

      setStatus('Inventory size added');
      await loadInventory();
    } catch (err) {
      setError(err.message || 'Failed to add inventory size');
    } finally {
      setSavingId(null);
    }
  };

  const changeLocal = (id, field, value) => {
    setInventory((items) =>
      items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
        <p className="mt-1 text-sm text-slate-500">
          Track fitted cap sizes, thresholds, and stock movement.
        </p>
      </div>

      {error ? <div className="status-banner-error">{error}</div> : null}
      {status ? <div className="status-banner-success">{status}</div> : null}

      <div className="grid gap-4 md:grid-cols-4">
        <div className="card p-5">
          <p className="text-sm text-slate-500">Total Inventory</p>
          <h2 className="text-3xl font-bold">{stats.totalUnits}</h2>
        </div>

        <div className="card p-5">
          <p className="text-sm text-slate-500">Product Styles</p>
          <h2 className="text-3xl font-bold">{stats.productStyles}</h2>
        </div>

        <div className="card p-5">
          <p className="text-sm text-slate-500">Low Stock</p>
          <h2 className="text-3xl font-bold text-orange-600">{stats.lowStock}</h2>
        </div>

        <div className="card p-5">
          <p className="text-sm text-slate-500">Sold Out</p>
          <h2 className="text-3xl font-bold text-red-600">{stats.soldOut}</h2>
        </div>
      </div>

      <div className="card p-5">
        <h2 className="mb-4 text-xl font-bold">Add New Size</h2>

        <div className="grid gap-3 md:grid-cols-5">
          <select
            className="select-field"
            value={newRow.product_id}
            onChange={(e) => setNewRow({ ...newRow, product_id: e.target.value })}
          >
            <option value="">Choose product</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>

          <input
            className="rounded-xl border border-slate-200 px-3 py-2 min-h-[44px]"
            placeholder="Size e.g. 7 1/4"
            value={newRow.size}
            onChange={(e) => setNewRow({ ...newRow, size: e.target.value })}
          />

          <input
            className="rounded-xl border border-slate-200 px-3 py-2 min-h-[44px]"
            type="number"
            min="0"
            placeholder="Qty"
            value={newRow.quantity}
            onChange={(e) => setNewRow({ ...newRow, quantity: e.target.value })}
          />

          <input
            className="rounded-xl border border-slate-200 px-3 py-2 min-h-[44px]"
            type="number"
            min="0"
            placeholder="Threshold"
            value={newRow.low_stock_threshold}
            onChange={(e) =>
              setNewRow({ ...newRow, low_stock_threshold: e.target.value })
            }
          />

          <button
            className="btn-accent"
            onClick={addSizeRow}
            disabled={savingId === 'new'}
          >
            {savingId === 'new' ? 'Adding...' : 'Add Size'}
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Size</th>
              <th>Quantity</th>
              <th>Threshold</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {inventory.map((item) => {
              const qty = Number(item.quantity || 0);
              const threshold = Number(item.low_stock_threshold || 0);

              let stockStatus = 'In Stock';
              let statusClass = 'text-green-700';

              if (qty === 0) {
                stockStatus = 'Sold Out';
                statusClass = 'text-red-700';
              } else if (qty <= threshold) {
                stockStatus = 'Low Stock';
                statusClass = 'text-orange-700';
              }

              return (
                <tr key={item.id}>
                  <td>{item.product_name}</td>

                  <td>
                    <input
                      className="w-24 rounded-xl border border-slate-200 px-3 py-2 min-h-[44px]"
                      value={item.size}
                      onChange={(e) => changeLocal(item.id, 'size', e.target.value)}
                    />
                  </td>

                  <td>
                    <input
                      className="w-24 rounded-xl border border-slate-200 px-3 py-2 min-h-[44px]"
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={(e) =>
                        changeLocal(item.id, 'quantity', Number(e.target.value))
                      }
                    />
                  </td>

                  <td>
                    <input
                      className="w-24 rounded-xl border border-slate-200 px-3 py-2 min-h-[44px]"
                      type="number"
                      min="0"
                      value={item.low_stock_threshold}
                      onChange={(e) =>
                        changeLocal(item.id, 'low_stock_threshold', Number(e.target.value))
                      }
                    />
                  </td>

                  <td>
                    <span className={`font-semibold ${statusClass}`}>
                      {stockStatus}
                    </span>
                  </td>

                  <td>
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="btn-secondary px-3 py-2 text-xs"
                        disabled={savingId === item.id}
                        onClick={() =>
                          updateItem(item.id, {
                            size: item.size,
                            quantity: qty + 1,
                            low_stock_threshold: threshold,
                          })
                        }
                      >
                        +1
                      </button>

                      <button
                        className="btn-secondary px-3 py-2 text-xs"
                        disabled={savingId === item.id}
                        onClick={() =>
                          updateItem(item.id, {
                            size: item.size,
                            quantity: Math.max(0, qty - 1),
                            low_stock_threshold: threshold,
                          })
                        }
                      >
                        -1
                      </button>

                      <button
                        className="btn-primary px-3 py-2 text-xs"
                        disabled={savingId === item.id}
                        onClick={() =>
                          updateItem(item.id, {
                            size: item.size,
                            quantity: Math.max(0, qty),
                            low_stock_threshold: Math.max(0, threshold),
                          })
                        }
                      >
                        {savingId === item.id ? 'Saving...' : 'Save'}
                      </button>

                      <button
                        className="btn-danger-soft px-3 py-2 text-xs"
                        disabled={savingId === item.id}
                        onClick={() => requestRemoveItem(item)}
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!inventory.length && !loading ? (
              <tr>
                <td colSpan="6">No inventory found.</td>
              </tr>
            ) : null}

            {loading ? (
              <tr>
                <td colSpan="6">Loading inventory...</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        confirmLabel={confirmModal?.confirmLabel || 'Confirm'}
        destructive={confirmModal?.destructive}
        loading={Boolean(savingId)}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />
    </div>
  );
}
